import { gql } from 'graphql-request';

/** Whether the area overlaps the site, or sits within 100 m of it. */
export type OverlapType = 'OVERLAPPING' | 'ADJACENT';

export type ProtectedArea = {
    /** Bilingual names ("Afon Teifi / River Teifi") are a single string. */
    name: string;
    /** `protected_area` for every area today; KBAs will arrive as another value. */
    type: string | null;
    /** OVERLAPPING means the area intersects the site footprint. */
    overlapType: OverlapType | null;
    /** Distance to the area's nearest edge. Zero when it overlaps. Not displayed. */
    distanceMetres: number | null;
    /** Area shared with the site. Only present when OVERLAPPING. */
    overlapAreaM2: number | null;
};

// `protectedAreas` is nested under `site` so it honours the page's selected
// time period (`at`) — which resolves both the site's boundary and the version
// of the protected-areas dataset in force then. Paging and `search` are both
// server-side; `pagination.totalItems` counts the whole (searched) list, so it
// drives both the widget's badge and the pager.
//
// The list is ordered nearest-first, and `sortDirection` reverses that rather
// than ordering names (DD-6078).
export const siteProtectedAreasQuery = gql`
    query SiteProtectedAreas(
        $organisationBusinessCentralId: String!
        $siteId: ID!
        $at: DateTime
        $page: Int!
        $pageSize: Int!
        $search: String
        $sortDirection: GqlSortDirection
        $overlapTypes: [OverlapType!]
    ) {
        site(organisationBusinessCentralId: $organisationBusinessCentralId, siteId: $siteId, at: $at) {
            hasProtectedAreasCoverage
            protectedAreas(
                page: $page
                pageSize: $pageSize
                search: $search
                sortDirection: $sortDirection
                overlapTypes: $overlapTypes
            ) {
                items {
                    name
                    type
                    overlapType
                    distanceMetres
                    overlapAreaM2
                }
                pagination {
                    totalItems
                }
            }
        }
    }
`;

export type SiteProtectedAreasResult = {
    site: {
        /** Whether the protected-areas layer covers this site's country. False makes an
         *  empty list mean "not surveyed yet" rather than "no protected areas here". */
        hasProtectedAreasCoverage: boolean;
        protectedAreas: {
            items: ProtectedArea[];
            pagination: {
                /** Count behind the current search, regardless of page size. */
                totalItems: number;
            };
        };
    } | null;
};
